import * as THREE from 'three';
import { audioCacheManager } from './audio-cache-manager.js';

class InputController {
  constructor(camera, domElement, wallController, doorController, windowController) {
    this.camera = camera;
    this.domElement = domElement;
    this.wallController = wallController;
    this.doorController = doorController;
    this.windowController = windowController;

    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();
    this.groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    
    // Per il doppio click sulle porte
    this.lastClickTime = 0;
    this.doubleClickDelay = 300; // ms
    
    // Flag per disabilitare comandi tastiera (usato da catalog-panel)
    this.keyboardEnabled = true;
    
    // Bind dei metodi per preservare il context
    this.onClick = this.onClick.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onDoubleClick = this.onDoubleClick.bind(this);

    // Touch
    this._onTouchStart = this._onTouchStart.bind(this);
    this._onTouchMove  = this._onTouchMove.bind(this);
    this._onTouchEnd   = this._onTouchEnd.bind(this);
    
    this.setupEventListeners();
  }
  
  setupEventListeners() {
    this.domElement.addEventListener('click', this.onClick);
    this.domElement.addEventListener('dblclick', this.onDoubleClick);
    this.domElement.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('keydown', this.onKeyDown);

    // Listener per il touch
    this.domElement.addEventListener('touchstart', this._onTouchStart, { passive: false });
    this.domElement.addEventListener('touchmove',  this._onTouchMove,  { passive: false });
    this.domElement.addEventListener('touchend',   this._onTouchEnd,   { passive: false });
  }

  // Crea un event-like object da un touch
  _touchToEvent(touch) {
    return { clientX: touch.clientX, clientY: touch.clientY };
  }

  _onTouchStart(event) {
    const touch = event.changedTouches[0];
    const synth = this._touchToEvent(touch);

    // Modalità disegno pareti: il trascinamento definisce la parete
    // touchstart = primo punto, touchmove = preview, touchend = fine parete
    if (this.wallController.drawingMode) {
      event.preventDefault();
      if (!this.wallController.isDrawing) {
        const point = this.getGroundIntersection(synth);
        if (point) this.wallController.startDrawing(point);
      }
      return;
    }

    // Doppio tap: due tap entro 300 ms
    const now = Date.now();
    const timeSinceLast = now - this.lastClickTime;
    this.lastClickTime = now;

    if (timeSinceLast < 300) {
      event.preventDefault();
      this.onDoubleClick(synth);
      return;
    }

    // Tap singolo in modalità porte/finestre
    if (this.doorController.placementMode || this.windowController.placementMode) {
      event.preventDefault();
      this.onClick(synth);
    }
  }

  _onTouchMove(event) {
    const touch = event.changedTouches[0];
    const synth = this._touchToEvent(touch);

    // Modalità disegno pareti: aggiorna la preview durante il drag
    if (this.wallController.isDrawing) {
      event.preventDefault();
      const point = this.getGroundIntersection(synth);
      if (point) {
        this.wallController.updateDrawing(point);
        this.wallController.updateMeasurementLabel(point, touch.clientX, touch.clientY);
      }
      return;
    }

    // Modalità porte/finestre: aggiorna il cursore di posizionamento
    if (this.doorController.placementMode || this.windowController.placementMode) {
      event.preventDefault();
      const point = this.getGroundIntersection(synth);
      if (!point) return;
      if (this.doorController.placementMode) {
        this.doorController.updatePreview(point, synth, this.camera);
      } else {
        this.windowController.updatePreview(point, synth, this.camera);
      }
    }
  }

  _onTouchEnd(event) {
    // Fine trascinamento in modalità disegno pareti: conclude la parete
    if (this.wallController.isDrawing) {
      event.preventDefault();
      const touch = event.changedTouches[0];
      const synth = this._touchToEvent(touch);
      const point = this.getGroundIntersection(synth);
      if (point) {
        this.wallController.finishDrawing(point);
      }
    }
  }
  
  onClick(event) {
    const point = this.getGroundIntersection(event);
    if (!point) return;
    
    // Gestione modalità pareti
    if (this.wallController.drawingMode) {
      if (!this.wallController.isDrawing) {
        this.wallController.startDrawing(point);
      } else {
        this.wallController.finishDrawing(point);
      }
      return;
    }
    
    // Gestione modalità porte
    if (this.doorController.placementMode) {
      this.doorController.placeDoor(point);
      return;
    }
    
    // Gestione modalità finestre
    if (this.windowController.placementMode) {
      this.windowController.placeWindow(point);
      return;
    }
  }
  
  onDoubleClick(event) {
    // Se si è in modalità editing, ignora
    if (this.wallController.drawingMode || 
        this.doorController.placementMode || 
        this.windowController.placementMode) {
      return;
    }

    // Cancella immediatamente qualsiasi selezione pendente di porte/finestre e mobili
    // (evita il flash dei bottoni al doppio click)
    window.app?.engine?.getSelectionController()?.cancelDoorWindowSelect();
    window.app?.engine?.getSelectionController()?.cancelFurnitureSelect();
    
    // Trova oggetti cliccati
    const intersects = this.getIntersectedObjects(event);
    
    if (intersects.length > 0) {
      const object = intersects[0].object;
      
      // Risale fino a trovare un gruppo con userData
      let current = object;
      while (current && !current.userData.door && !current.userData.window && !current.userData.furniture) {
        current = current.parent;
      }
      
      if (current && current.userData.door) {
        window.app?.engine?.getSelectionController()?.deselectAll();
        const door = current.userData.door;
        door.toggle();
        console.log('Porta', door.isOpen ? 'aperta' : 'chiusa');
      } else if (current && current.userData.window) {
        window.app?.engine?.getSelectionController()?.deselectAll();
        const win = current.userData.window;
        win.toggle();
        console.log('Finestra', win.isOpen ? 'aperta' : 'chiusa');
      } else if (current && current.userData.furniture) {
        const furniture = current.userData.furniture;
        window.app?.engine?.getSelectionController()?.deselectAll();
        this._playFurnitureAudio(furniture);
      }
    }
  }
  
  onMouseMove(event) {
    const point = this.getGroundIntersection(event);
    if (!point) return;
    
    // Aggiorna preview in base alla modalità attiva
    if (this.wallController.isDrawing) {
      this.wallController.updateDrawing(point);
      // Aggiorna l'etichetta con la lunghezza e la posizione del cursore
      this.wallController.updateMeasurementLabel(point, event.clientX, event.clientY);
    } else if (this.doorController.placementMode) {
      // Passa l'evento mouse e la camera per il raycast
      this.doorController.updatePreview(point, event, this.camera);
    } else if (this.windowController.placementMode) {
      // Passa l'evento mouse e la camera per il raycast
      this.windowController.updatePreview(point, event, this.camera);
    }
  }
  
  getIntersectedObjects(event) {
    const rect = this.domElement.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    
    this.raycaster.setFromCamera(this.mouse, this.camera);
    
    // Raccoglie tutti gli oggetti interattivi
    const objects = [];
    
    this.doorController.getAllDoors().forEach(door => {
      if (door.mesh) {
        door.mesh.traverse(child => {
          if (child.isMesh) objects.push(child);
        });
      }
    });
    
    this.windowController.getAllWindows().forEach(window => {
      if (window.mesh) {
        window.mesh.traverse(child => {
          if (child.isMesh) objects.push(child);
        });
      }
    });

    const furnitureController = window.app?.engine?.getFurnitureController();
    if (furnitureController) {
      furnitureController.getAllFurniture().forEach(furniture => {
        if (furniture.mesh) {
          furniture.mesh.traverse(child => {
            if (child.isMesh) objects.push(child);
          });
        }
      });
    }
    
    return this.raycaster.intersectObjects(objects);
  }
  
  onKeyDown(event) {
    // IMPORTANTE: Ignora eventi che provengono da campi di input
    // Questo permette di scrivere liberamente in qualsiasi input senza interferenze
    const target = event.target;
    if (target.tagName === 'INPUT' || 
        target.tagName === 'TEXTAREA' || 
        target.tagName === 'SELECT' ||
        target.isContentEditable) {
      return; // Non gestire scorciatoie se si sta scrivendo in un campo
    }
    
    // Se la tastiera è disabilitata, ignora
    if (!this.keyboardEnabled) return;
    
    // ESC per annullare qualsiasi operazione
    if (event.key === 'Escape') {
      this.wallController.cancelDrawing();
      this.doorController.disablePlacementMode();
      this.windowController.disablePlacementMode();
      
      if (window.app) {
        window.app.updateAllButtons();
      }
      return;
    }
    
    // W per toggle modalità disegno pareti
    if (event.key === 'w' || event.key === 'W') {
      this.doorController.disablePlacementMode();
      this.windowController.disablePlacementMode();
      this.wallController.toggleDrawMode();
      
      if (window.app) {
        window.app.updateAllButtons();
      }
      event.preventDefault();
      return;
    }
    
    // R per ruotare oggetti selezionati
    if (event.key === 'r' || event.key === 'R') {
      if (window.app) {
        window.app.rotateSelected();
      }
      event.preventDefault();
      return;
    }
    
    // D per duplicare oggetti selezionati
    if (event.key === 'd' || event.key === 'D') {
      if (window.app) {
        window.app.duplicateSelected();
      }
      event.preventDefault();
      return;
    }
    
    // Delete/Canc per eliminare oggetti selezionati
    if (event.key === 'Delete' || event.key === 'Backspace') {
      if (window.app) {
        window.app.deleteSelected();
      }
      event.preventDefault();
      return;
    }
    
    // O per aprire/chiudere tutte le porte e finestre
    if (event.key === 'o' || event.key === 'O') {
      if (window.app) {
        window.app.toggleAllOpenings();
      }
      event.preventDefault();
      return;
    }
  }
  
  
  enableKeyboard() {
    this.keyboardEnabled = true;
  }
  
  disableKeyboard() {
    this.keyboardEnabled = false;
  }
  
  getGroundIntersection(event) {
    const rect = this.domElement.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    
    this.raycaster.setFromCamera(this.mouse, this.camera);
    
    const intersection = new THREE.Vector3();
    this.raycaster.ray.intersectPlane(this.groundPlane, intersection);
    
    return intersection;
  }
  
  // Delega la riproduzione audio all'AudioCacheManager
  _playFurnitureAudio(furniture) {
    const id = furniture.catalogItem?.id;
    const name = furniture.catalogItem?.name;
    if (id) audioCacheManager.play(id, name);
  }

  dispose() {
    this.domElement.removeEventListener('click', this.onClick);
    this.domElement.removeEventListener('dblclick', this.onDoubleClick);
    this.domElement.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('keydown', this.onKeyDown);
    this.domElement.removeEventListener('touchstart', this._onTouchStart);
    this.domElement.removeEventListener('touchmove',  this._onTouchMove);
    this.domElement.removeEventListener('touchend',   this._onTouchEnd);
  }
}

export default InputController;